import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Loader2, ExternalLink, Briefcase, ClipboardList, TrendingUp, AlarmClock } from "lucide-react";
import { LiveExperience } from "@/components/LiveExperience";

const OB_STYLES = {
  not_started: "bg-elevated border-steel text-slate-500",
  in_progress: "bg-amber-100 border-amber-300 text-amber-800",
  complete: "bg-emerald-100 border-emerald-300 text-emerald-800",
};
const OB_LABELS = { not_started: "Not Started", in_progress: "In Progress", complete: "Complete" };
const LEVEL_STYLES = {
  developing: "bg-amber-100 border-amber-300 text-amber-800",
  consistent: "bg-sky-100 border-sky-300 text-sky-800",
  competent: "bg-emerald-100 border-emerald-300 text-emerald-800",
};
const OBJ_STYLES = {
  not_started: "bg-elevated border-steel text-slate-500",
  in_progress: "bg-amber-100 border-amber-300 text-amber-800",
  completed: "bg-emerald-100 border-emerald-300 text-emerald-800",
  further_development_required: "bg-red-100 border-red-300 text-red-700",
};
const OBJ_LABELS = { not_started: "Not Started", in_progress: "In Progress", completed: "Completed", further_development_required: "Further Development" };

function Section({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <section className="bg-white border border-steel rounded-md p-5 sm:p-6" data-testid={testid}>
      <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-lomond" />} {title}
      </h2>
      {subtitle && <p className="text-xs text-slate-500 mt-1 mb-4">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </section>
  );
}

export default function Development() {
  const [data, setData] = useState(null);
  const [acking, setAcking] = useState(false);

  const load = () => api.get("/development/me").then((r) => setData(r.data)).catch(() => toast.error("Could not load your development record"));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="development-loading" />;

  const done = data.onboarding_items.filter((i) => i.status === "complete").length;
  const currentPlan = data.plans.find((p) => !p.manager_signoff) || data.plans[data.plans.length - 1];

  const acknowledge = async () => {
    setAcking(true);
    try {
      await api.post(`/development/action-plans/${currentPlan.plan_number}/acknowledge`);
      toast.success("Thanks — action plan acknowledged.");
      load();
    } catch (e) { toast.error("Could not acknowledge right now."); } finally { setAcking(false); }
  };

  return (
    <div className="space-y-6" data-testid="development-page">
      <section className="bg-charcoal text-white rounded-md p-6" data-testid="development-header">
        <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond">
          {data.operator.operator_grade === "competent" ? "Lomond Competent Operator" : "Qualified Development Operator"}
        </p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl mt-1" data-testid="development-heading">
          {data.operator.name} — Week {data.weeks_employed}
        </h1>
        <p className="text-white/60 text-sm mt-1">
          {data.operator.operator_grade === "competent"
            ? "Outstanding — you've earned Lomond Competent Operator status. Keep setting the standard."
            : "You did it. Now it's about building real experience, one good week at a time."}
        </p>
      </section>

      {data.review?.in_progress && (
        <div className="bg-sky-50 border border-sky-300 rounded-md p-4" data-testid="review-in-progress-banner">
          <p className="text-sm text-sky-800 font-semibold">COMPETENCE REVIEW IN PROGRESS — management is reviewing your evidence pack. Nothing for you to do; keep working well.</p>
        </div>
      )}

      {!data.review?.in_progress && data.review?.last?.decision === "continue_development" && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4" data-testid="continuing-development-banner">
          <p className="text-sm font-bold text-amber-800 uppercase">Continuing Development</p>
          <p className="text-sm text-amber-800 mt-1">Agreed objectives: {data.review.last.new_objectives}</p>
          <p className="text-xs text-amber-700 mt-1">Next review: {data.review.last.next_review_date}</p>
        </div>
      )}

      {data.reminders.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 space-y-1" data-testid="development-reminders">
          {data.reminders.map((r, i) => (
            <p key={i} className="text-sm text-amber-800 flex items-center gap-2"><AlarmClock className="h-4 w-4 shrink-0" /> {r.message}</p>
          ))}
        </div>
      )}

      <Section title="Workplace Onboarding" subtitle="Complete your workplace onboarding before full live-site deployment."
        icon={ClipboardList} testid="onboarding-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <p className="text-sm text-slate-600">{done}/10 items complete</p>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono uppercase tracking-widest ${
            data.workplace_ready.ready ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-elevated border-steel text-slate-500"
          }`} data-testid="workplace-ready-chip">
            Workplace Ready: {data.workplace_ready.ready ? "YES" : "NO"}
          </span>
        </div>
        <ul className="space-y-2" data-testid="onboarding-list">
          {data.onboarding_items.map((i) => (
            <li key={i.key} className="flex items-center justify-between gap-3 bg-elevated rounded-sm px-3.5 py-2.5" data-testid={`onboarding-item-${i.key}`}>
              <span className="text-sm text-charcoal flex items-center gap-2">
                {i.status === "complete" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                 : i.status === "in_progress" ? <Loader2 className="h-4 w-4 text-amber-500 shrink-0" />
                 : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                {i.label}
              </span>
              <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest shrink-0 ${OB_STYLES[i.status]}`}>{OB_LABELS[i.status]}</span>
            </li>
          ))}
        </ul>
        {data.connecteam.url && (
          <div className="mt-4 pt-4 border-t border-steel">
            <a href={data.connecteam.url} target="_blank" rel="noreferrer">
              <Button className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="connecteam-open-btn">
                <ExternalLink className="h-4 w-4 mr-1.5" /> Open Connecteam
              </Button>
            </a>
            {data.connecteam.note && <p className="text-xs text-slate-500 mt-2" data-testid="connecteam-note">{data.connecteam.note}</p>}
          </div>
        )}
      </Section>

      {currentPlan && (
        <Section title={`My Development Plan — Action Plan ${currentPlan.plan_number} (${currentPlan.period_label})`}
          subtitle={`Started ${currentPlan.start_date} — target review ${currentPlan.target_review_date}`}
          icon={TrendingUp} testid="development-plan-card">
          <div className="space-y-3" data-testid="plan-objectives">
            {currentPlan.objectives.map((o, i) => (
              <div key={o.id || i} className="bg-elevated rounded-md p-3.5" data-testid={`my-objective-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-charcoal">{i + 1}. {o.title}</p>
                  <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest shrink-0 ${OBJ_STYLES[o.status] || OBJ_STYLES.not_started}`}>{OBJ_LABELS[o.status] || o.status}</span>
                </div>
                {o.target && <p className="text-xs text-slate-600 mt-1.5"><span className="font-semibold">Target:</span> {o.target}</p>}
                {o.auto_evidence && <p className="text-xs text-emerald-700 mt-1 border-l-2 border-emerald-400 pl-2.5" data-testid={`my-evidence-${i}`}>{o.auto_evidence}</p>}
                {o.progress && <p className="text-xs text-slate-500 mt-1"><span className="font-semibold">Progress:</span> {o.progress}</p>}
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-steel flex items-center gap-3 flex-wrap">
            {currentPlan.employee_ack ? (
              <p className="text-sm text-emerald-700 font-semibold flex items-center gap-2" data-testid="plan-ack-done">
                <CheckCircle2 className="h-4 w-4" /> You acknowledged this plan
              </p>
            ) : (
              <Button onClick={acknowledge} disabled={acking} variant="outline" className="border-steel text-charcoal bg-white" data-testid={`plan-ack-btn-${currentPlan.plan_number}`}>
                {acking ? "Saving..." : "I've read this — acknowledge plan"}
              </Button>
            )}
            {currentPlan.manager_signoff && (
              <p className="text-xs text-slate-500">Signed off by {currentPlan.manager_signoff.by_name} — {currentPlan.completion_date}</p>
            )}
          </div>
        </Section>
      )}

      <Section title="My Live Experience" subtitle="After your weekly Connecteam timesheet, submit your Live Experience for the same period — Lomond reviews and verifies every record."
        icon={Briefcase} testid="live-experience-card">
        <LiveExperience data={data} reload={load} />
      </Section>

      <Section title="My Progress" subtitle="How Lomond sees your competence developing right now." icon={TrendingUp} testid="competence-card">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5" data-testid="competence-grid">
          {data.competence.map((c) => (
            <div key={c.key} className="bg-elevated rounded-sm p-3 text-center" data-testid={`competence-${c.key}`}>
              <p className="text-xs font-semibold text-charcoal">{c.label}</p>
              <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${LEVEL_STYLES[c.level]}`}>{c.level}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-4">Competence is earned through evidence — never just time served. Keep building good weeks.</p>
      </Section>
    </div>
  );
}
